import { useMemo } from 'react';

import { ExperienceName } from '@atlassian/conversation-assistant-instrumentation/constants/experience-tracker';
import { useAIMateExperienceTracker } from '@atlassian/conversation-assistant-instrumentation/main';
import type { ApiError } from '@atlassian/conversation-assistant-service-api/utils/api-error';
import { useQuery } from '@atlassian/conversation-assistant-service/common/utils/use-query';
import { performGqlFetch } from '@atlassian/conversation-assistant-utils/graphqlUtils';

import { useSiteContext } from '../site-id/index.tsx';
import { useTenantDataController } from '../tenant-data/index.tsx';

import {
	type TeamSearchResponse,
	type TeamSearchResult,
	type TeamSearchVariables,
} from './types.ts';

const TeamSearchQuery = `
query TeamSearch($orgAri: ID!, $siteId: String!, $userId: ID!) {
  team {
    teamSearchV2(
      organizationId: $orgAri
      siteId: $siteId
      filter: {membership: {memberIds: [$userId]}}
    ) @optIn(to: "Team-search-v2") {
      nodes {
        team {
          displayName
          id
          smallAvatarImageUrl
        }
        memberCount
      }
    }
  }
}
`;

type UseTeamsControllerProps = {
	/** Literal ID or an ARI */
	userId?: string | null;
};

export const useTeamsController = ({
	userId: userIdProp,
}: UseTeamsControllerProps): {
	data: TeamSearchResult[] | null;
	error: ApiError | null;
	isFetching: boolean;
	refetch: () => Promise<TeamSearchResult[] | null>;
} => {
	const { siteId } = useSiteContext();
	const { data: tenantData } = useTenantDataController(siteId);
	const experienceTracker = useAIMateExperienceTracker();
	const orgAri = useMemo(() => {
		if (!tenantData?.orgId) {
			return '';
		}

		return `ari:cloud:platform::org/${tenantData.orgId}`;
	}, [tenantData?.orgId]);

	const userId = useMemo(() => {
		if (!userIdProp) {
			return;
		}

		// format is ari:cloud:identity::user/123456
		return userIdProp.split('/').pop();
	}, [userIdProp]);

	const query = useQuery({
		queryKey: ['teamSearch', orgAri, siteId, userId ?? ''],
		queryFn: async (): Promise<TeamSearchResult[]> => {
			experienceTracker.start({
				id: ExperienceName.FETCH_AGENT_TEAM_DATA,
				name: ExperienceName.FETCH_AGENT_TEAM_DATA,
			});
			const response = await performGqlFetch<TeamSearchResponse, TeamSearchVariables>({
				gqlQuery: TeamSearchQuery,
				variables: {
					orgAri,
					siteId,
					userId: userId ?? '',
				},
			});
			return response.data.team.teamSearchV2.nodes;
		},
		enabled: !!orgAri.length && !!siteId.length && !!userId?.length,
		onSuccess: () => {
			experienceTracker.succeed({
				name: ExperienceName.FETCH_AGENT_TEAM_DATA,
			});
		},
		onError: (error) => {
			experienceTracker.fail({
				name: ExperienceName.FETCH_AGENT_TEAM_DATA,
				error,
			});
		},
	});

	return query;
};
